'use client'

import { useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import PostList from "./PostList"
import type { PostMetadata } from "@/lib/PostMetadata"

interface PostsPaginationProps {
  posts: PostMetadata[]
}

const POSTS_PER_PAGE = 10

const PostsPagination = ({ posts }: PostsPaginationProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE))
  const pageParam = Number(searchParams.get('page') || '1')
  const currentPage = Number.isInteger(pageParam) && pageParam > 0
    ? Math.min(pageParam, totalPages)
    : 1

  const currentPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE
    return posts.slice(start, start + POSTS_PER_PAGE)
  }, [currentPage, posts])

  const changePage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return
    router.push(`/posts?page=${page}`, { scroll: false })
  }

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }, [totalPages])

  return (
    <div className="mt-2 sm:mt-20">
      <div className="min-h-[60vh]">
        {currentPosts.map((post) => (
          <div key={post.slug}>
            <PostList {...post} page={currentPage} />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="py-6 flex items-center justify-center gap-2 flex-wrap">
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-sm text-neutral-500 disabled:opacity-40"
          >
            上一页
          </button>

          <div className="flex items-center gap-2 flex-wrap">
            {pageNumbers.map((page) => (
              <button
                key={page}
                onClick={() => changePage(page)}
                className={`px-2 py-1 text-sm transition-colors ${page === currentPage
                  ? 'text-neutral-900 dark:text-neutral-100 font-medium'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                  }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-sm text-neutral-500 disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  )
}

export default PostsPagination
